import { createApp, App as Application } from 'vue'
import App from '@/App.vue'
import { createPinia } from 'pinia'
import * as Sentry from '@sentry/vue'
import { Wasm as SentryWasm } from '@sentry/wasm'
import { ExtraErrorData as SentryExtraError } from '@sentry/integrations'
import { BrowserSupport, getBrowserSupport } from '@/utils/support-utility'
import HardwareAccelerationRequired from '@/components/unsupported-pages/HardwareAccelerationRequired.vue'
import UnsupportedBrowser from '@/components/unsupported-pages/UnsupportedBrowser.vue'
import UnsupportedDevice from '@/components/unsupported-pages/UnsupportedDevice.vue'
import { createGtm } from '@gtm-support/vue-gtm'
import * as PIXI from 'pixi.js'
import { injectGtm } from './utils/gtm'

import '@/sass/global.scss'

const browserSupport: BrowserSupport = getBrowserSupport()

// Inject GTM container script into document's head
if (import.meta.env.VUE_APP_GTM_ENABLED === 'true') {
  // Initialise datalayer for injecting the GTM script and tracking page views on route changes
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' })
  injectGtm('GTM-WRQHXNDV')
}

const gtm = createGtm({
  id: 'GTM-WRQHXNDV',
  enabled: false,
  debug: false,
  loadScript: false
})

const getApp = (): Application => {
  if (browserSupport.unsupportedOS) {
    const app = createApp(UnsupportedDevice)
    return app
  }
  if (!browserSupport.supported) {
    const app = createApp(UnsupportedBrowser)
    app.use(gtm)
    return app
  }
  if (!PIXI.utils.isWebGLSupported()) {
    const app = createApp(HardwareAccelerationRequired)
    return app
  }

  const app = createApp(App)
  Sentry.init({
    app,
    enabled: import.meta.env.MODE !== 'development',
    dsn: 'https://6361e2da53034f278ba724f5582c1c76@o263013.ingest.sentry.io/6667145',
    integrations: [
      Sentry.browserTracingIntegration(),
      new SentryWasm(),
      new SentryExtraError({
        depth: 2
      })
    ],
    tracesSampleRate: import.meta.env.PROD ? 0.5 : 1.0,
    environment: import.meta.env.MODE,
    release: import.meta.env.SENTRY_RELEASE
  })
  app.use(createPinia())
  app.use(gtm)
  return app
}

const app = getApp()
app.mount('#app')

export { app }
